/* eslint-disable @typescript-eslint/no-use-before-define */
import { InMemoryCache, defaultDataIdFromObject } from '@apollo/client';
import type { StoreObject } from '@apollo/client/utilities';
import calculateDataGridCacheId from '../components/dataGrid/utils/calculateDataGridCacheId';
import generatedIntrospection, { DataGridResult, TypedTypePolicies } from '../gql/graphql';
import { FlashMessage } from './cache/flashMessages';

const { possibleTypes } = generatedIntrospection;

// TODO there's probably a better way to solve this
type QueryFields = {
  Query: {
    fields: {
      flashMessages: object;
      fatalError: object;
    };
  };
};

const typePolicies: TypedTypePolicies | QueryFields = {
  Query: {
    fields: {
      // Add root query fields that return reactive var values
      flashMessages: {
        read() {
          return flashMessagesVar();
        },
      },
      fatalError: {
        read() {
          return fatalErrorVar();
        },
      },
    },
  },
  Company: {
    fields: {
      // Resolve objects when requesting with id
      mailTemplates: {
        read(existing, { args, toReference }) {
          if (args?.id) {
            return [
              toReference({
                __typename: 'MailTemplate',
                id: args.id,
              }),
            ];
          }

          return existing;
        },
      },
      shipmentPresets: {
        read(existing, { args, toReference }) {
          if (args?.id) {
            return [
              toReference({
                __typename: 'ShipmentPreset',
                id: args.id,
              }),
            ];
          }

          return existing;
        },
      },
      warehouses: {
        read(existing, { args, toReference }) {
          if (args?.id) {
            return [
              toReference({
                __typename: 'Warehouse',
                id: args.id,
              }),
            ];
          }

          return existing;
        },
      },
      paymentSources: {
        read(existing, { args, toReference }) {
          if (args?.id) {
            return [
              toReference({
                __typename: 'PaymentSource',
                id: args.id,
              }),
            ];
          }
          return existing;
        },
      },
    },
  },
  // The settings types have no id, but can safely be merged
  // https://www.apollographql.com/docs/react/caching/cache-field-behavior/#configuring-merge-functions-for-types-rather-than-fields
  GlobalSettings: {
    merge: true,
  },
  CompanySettings: {
    merge: true,
  },
};

const dataIdFromObject = (object: Readonly<StoreObject>) => {
  if (object.__typename === 'DataGridResult') {
    return calculateDataGridCacheId(object as DataGridResult);
  }

  return defaultDataIdFromObject(object);
};

export const cache: InMemoryCache = new InMemoryCache({
  typePolicies,
  possibleTypes,
  dataIdFromObject,
});

/**
 * Set initial values when we create cache variables.
 */
export const flashMessagesVar = cache.makeVar<Array<FlashMessage>>([]);
export const fatalErrorVar = cache.makeVar<string | null>(null);
export const appVersionVar = cache.makeVar<string | null>(null);
export const loggedInVar = cache.makeVar<boolean | null>(null);
